import * as api from "./adapter";
import { convertItem } from "./convertors/vessels";

// CREW-1024 — the list filter is the URL-owned object from
// features/vessels/urlFilters.js. The drawer's "In management" Yes / No maps
// onto the backend's tri-state `is_managed`; an unset key is its third value,
// `all`. The old `is_selected` passthrough is gone: nothing in the UI ever set
// it (the tile view that did is not mounted anywhere).
const IS_MANAGED = { true: "managed", false: "not_managed" };

// CREW-1023 — the list asks for the columns it shows and nothing else. Without
// a sparse fieldset the resource serialises crew_list, five history blobs and
// raw_data_equasis for every row — none of it visible here — and the backend
// keys its preloads off exactly this list (VesselResource.populate_preloads).
export const LIST_FIELDS = [
  "imo", "name", "in_management", "equasis_type_name", "vessel_type_name",
  "dwt", "year_built", "engine_manufacturer", "engine_type", "kwt",
  "ism_manager", "owner_id", "owner_name", "updated_at",
  // CREW-1243 — the row draws an `AD` marker from this, and a sparse fieldset
  // means an attribute the list does not ask for simply never arrives. Found by
  // testing against production data: the four `_AD_…` vessels rendered with no
  // marker at all, because `attributes.advertising` was undefined rather than
  // false. The component's own test passes the flag in as a prop, so it could
  // not see this.
  "advertising",
];

const isBlank = (value) =>
  value === undefined || value === null || `${value}`.trim().length === 0;

// Option lists travel as comma-joined ids — what a JR filter takes.
const idList = (options) =>
  (Array.isArray(options) ? options : [])
    .map((option) => (option && typeof option === "object" ? option.value : option))
    .filter((id) => !isBlank(id))
    .join(",");

/**
 * buildIndexQuery
 * Translate the URL-owned filter into the names VesselResource filters on
 * (CREW-1025/1026/1027). The user thinks in age, the API in build year: "from
 * 5 years old" is "built no later than 5 years ago", so the bounds swap ends.
 * @param {Object} featureState - {pagination, sort, filter, search}
 * @param {Object} [options] - { today } for a deterministic age → year
 * @returns {Object} the JSON:API query for GET /vessels
 */
export const buildIndexQuery = ({ pagination, sort, filter = {}, search }, { today = new Date() } = {}) => {
  const query = {
    name: search,
    is_managed: IS_MANAGED[filter.managed] || "all",
    // CREW-1243 — only travels when set: the backend narrows on any value it is
    // given, so an unset filter must not appear in the query at all.
    ...(filter.advertising ? { advertising: filter.advertising } : {}),
  };

  const fleetTypes = idList(filter.fleet_type);
  if (fleetTypes) query.fleet_type_id = fleetTypes;
  const types = idList(filter.type);
  if (types) query.equasis_vessel_type_id = types;

  const dwt = filter.dwt || {};
  if (!isBlank(dwt.from)) query.dwt_from = `${dwt.from}`;
  if (!isBlank(dwt.to)) query.dwt_to = `${dwt.to}`;

  const age = filter.age || {};
  const thisYear = today.getFullYear();
  if (!isBlank(age.from)) query.year_built_to = `${thisYear - Number(age.from)}`;
  if (!isBlank(age.to)) query.year_built_from = `${thisYear - Number(age.to)}`;

  if (!isBlank(filter.engine)) query.engine = `${filter.engine}`.trim();

  // Names with an "including history" flag (CREW-1027 on the backend). The
  // flag travels only when it is on — absent means current names only.
  if (!isBlank(filter.ism)) query.ism_manager = `${filter.ism}`.trim();
  if (filter.ism_history === true) query.ism_manager_history = "true";
  if (!isBlank(filter.commercial)) query.commercial_manager = `${filter.commercial}`.trim();
  if (filter.commercial_history === true) query.commercial_manager_history = "true";

  // Our owner is a linked record (owner_ids, a filter other clients already
  // use); the registered owner is a name in the Equasis history.
  const owners = idList(filter.owner);
  if (owners) query.owner_ids = owners;
  if (!isBlank(filter.reg_owner)) query.registered_owner = `${filter.reg_owner}`.trim();
  if (filter.reg_owner_history === true) query.registered_owner_history = "true";

  return {
    filter: query,
    fields: { vessels: LIST_FIELDS.join(",") },
    sort: `${sort.direction === "asc" ? "" : "-"}${sort.field}`,
    page: { size: pagination.itemsPerPage, number: pagination.current },
  };
};

export default {
  /**
   * index
   * @param {Object} featureState - the vessels slice ({pagination, sort, filter, search})
   * @returns {Promise<*>}
   */
  index: (featureState) => api.read("vessels", { query: buildIndexQuery(featureState) }),
  show: async (id) => {
    const { data: json } = await api.read(`vessels/${id}`, {
      query: {
        include:
          "owner,manager,manager_vessel,owner_vessel,coordinator,recruiters",
      },
    });

    return convertItem(json);
  },
  /**
   * createOwner
   * @param attributes
   * @returns {Promise}
   */
  create: (attributes) => api.create({ type: "vessels", attributes }),
  /**
   * updateOwner
   * @param id
   * @param attributes
   * @returns {Promise}
   */
  update: (id, attributes) => api.update({ id, type: "vessels", attributes }),
  /**
   * deleteOwner
   * @returns {Promise<*>}
   */
  delete: (id) => api.destroy({ id, type: "vessels" }),
  /**
   * setManager
   * @param vesselId
   * @param managerId
   * @return {Promise}
   */
  setManager: (vesselId, managerId) =>
    api.create({
      type: "manager_vessels",
      relationships: {
        manager: { data: { type: "managers", id: managerId } },
        vessel: { data: { type: "vessels", id: vesselId } },
      },
    }),
  /**
   * setOwner
   * @param vesselId
   * @param ownerId
   * @return {Promise}
   */
  setOwner: (vesselId, ownerId) =>
    api.create({
      type: "owner_vessels",
      relationships: {
        owner: { data: { type: "owners", id: ownerId } },
        vessel: { data: { type: "vessels", id: vesselId } },
      },
    }),
  /**
   * changeManager
   * @param id
   * @param managerId
   * @return {Promise}
   */
  changeManager: (id, managerId) =>
    api.update({
      id,
      type: "manager_vessels",
      relationships: {
        manager: { data: { type: "managers", id: managerId } },
      },
    }),
  /**
   * changeOwner
   * @param id
   * @param ownerId
   * @return {Promise}
   */
  changeOwner: (id, ownerId) =>
    api.update({
      id,
      type: "owner_vessels",
      relationships: {
        owner: { data: { type: "owners", id: ownerId } },
      },
    }),
  /**
   * deleteManager
   * @param {String} id is managerVesselId
   * @return {Promise}
   */
  deleteManager: (id) =>
    api.destroy({
      id,
      type: "manager_vessels",
    }),
  /**
   * deleteOwner
   * @param {String} id is ownerVesselId
   * @return {Promise}
   */
  deleteOwner: (id) =>
    api.destroy({
      id,
      type: "owner_vessels",
    }),
  copyManningScale: (fromId, toId) =>
    api.create({
      type: "duplicate_manning_scales",
      attributes: {
        from_vessel_id: fromId,
        to_vessel_id: toId,
      },
    }),

  printCrewRole: async (vId) => {
    const defaultOptions = api.getDefaultOptions();
    const url = `/api/print/crew_role?vessel_id=${vId}&type=on_board`;
    const response = await fetch(url, defaultOptions);
    const blob = await response.blob();
    const filename = response.headers
      .get("Content-Disposition")
      .split("filename=")[1]
      .replaceAll('"', "");
    const uri = window.URL.createObjectURL(blob);

    return {
      uri,
      name: filename,
    };
  },

  updateRelation: async (id, relation, data) => {
    await api.apiRequest(`/api/vessels/${id}/relationships/${relation}`, {
      method: "PATCH",
      body: JSON.stringify({ data, update_from_equasis: false }),
    });
  },

  // CREW-1300 — just the photo fields, for polling while a photo is being
  // fetched in the background. A sparse fieldset keeps it cheap: the full
  // vessel serialises its crew list and five history blobs.
  // → { photo, photo_credit, photo_pending }
  photo: async (id) => {
    const { data: json } = await api.read(`vessels/${id}`, {
      query: { fields: { vessels: "photo,photo_credit,photo_pending" } },
    });
    return json?.data?.attributes || {};
  },

  // → { status: 'attached' | 'unchanged' | 'not_found', photo, photo_credit }, or a 422
  // with a sentence when the photo source did not answer.
  updatePhoto: async (payload) => {
    const { data } = await api.apiRequest(`/api/vessels/update_photo`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return data;
  },
};
